import * as React from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  Table as TableType,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";

import { ScrollBar, ScrollArea } from "@/components/ui/ScrollArea";
import { DataTableViewOptions } from "@/components/admin/tickets/component/DataTableViewOption";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading: boolean;
  showColumnSettings?: boolean;
  searchMessage?: string;
  tableInstance?: TableType<TData>
  onTableInit?: (table: TableType<TData>) => void;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading = false,
  showColumnSettings = false,
  searchMessage = "No records found",
  onTableInit,
  tableInstance
}: Readonly<DataTableProps<TData, TValue>>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});

  const [rowSelection, setRowSelection] = React.useState({});

  const table = tableInstance ? tableInstance : useReactTable({
    data: data,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  React.useEffect(() => {
    if (onTableInit) {
      onTableInit(table);
    }
  }, [table, onTableInit]);


  const renderTableContent = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length} className="h-24 text-center">
            <div className="flex space-x-2 justify-center items-center bg-white">
              {["-0.3s", "-0.15s", "0s"].map((delay, index) => (
                <div
                  key={index as number}
                  className="size-3 bg-black rounded-full animate-bounce"
                  style={{ animationDelay: delay }}
                ></div>
              ))}
            </div>
          </TableCell>
        </TableRow>
      );
    }

    if (table.getRowModel().rows.length) {
      return table.getRowModel().rows.map((row) => (
        <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
          {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </TableRow>
      ));
    }

    return (
      <TableRow>
        <TableCell colSpan={columns.length} className="h-24 text-center">
          {searchMessage}
        </TableCell>
      </TableRow>
    );
  };

  return (

    <div className="rounded-none border border-border">
      <ScrollArea className="w-full relative" hideScrollToTop={true}>
        <ScrollBar
          orientation="horizontal"
          className="relative z-30 cursor-grab h-2"
        />
        <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]">
          <TableHeader className="bg-gray-100">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>{renderTableContent()}</TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}
